import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RefreshCw, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';

interface PriceTimelineProps {
  comparisonProducts: any[];
  shouldLoad?: boolean;
  priceHistoryData?: any;
  loading?: boolean;
}

interface PriceHistoryPoint {
  fecha: string;
  tomaId: number;
  tableName: string;
  data?: any;
  // Price fields from server response
  precio?: number | string;
  precioOriginal?: number | string;
  precioDesc?: number | string | null;
  precioVenta?: number | string | null;
  // Alternative field names
  sale_price?: string;
  original_price?: string;
  discounted_price?: string;
  precio_venta?: string;
  precio_original?: string;
  precio_desc?: string;
  price?: string;
  ORIGINAL_PRICE?: string;
  DISCOUNTED_PRICE?: string;
  // Other fields
  marca?: string;
  titulo?: string;
  sitio?: string;
  url?: string;
}

const PriceTimeline: React.FC<PriceTimelineProps> = ({ 
  comparisonProducts, 
  shouldLoad = true, 
  priceHistoryData, 
  loading: parentLoading 
}) => {
  const { t } = useTranslation();
  const [localLoading, setLocalLoading] = useState(false);
  const [priceData, setPriceData] = useState<any[]>([]);
  const [products, setProducts] = useState<{[key: string]: any}>({});
  const [visibleProducts, setVisibleProducts] = useState<{[key: string]: boolean}>({});
  
  const loading = parentLoading !== undefined ? parentLoading : localLoading;

  // Colores para las líneas
  const colors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1',
    '#d084d0', '#ff9f9f', '#87d068', '#ffd700', '#ff6347'
  ];

  // Crear array ordenado de productIds basándose en el orden de comparisonProducts
  // Esto garantiza que los colores sean consistentes entre el gráfico y las tarjetas
  const orderedProductIds = React.useMemo(() => {
    return comparisonProducts
      .map(p => {
        const rawId = p.id_catalogo || p.ID_CATALOGO || p.id;
        return rawId ? String(rawId) : null;
      })
      .filter((id): id is string => id !== null && products[id] !== undefined);
  }, [comparisonProducts, products]);

  // Toggle visibilidad de producto
  const toggleProductVisibility = (productId: string) => {
    setVisibleProducts(prev => ({
      ...prev,
      [productId]: !prev[productId],
      [`${productId}_original`]: !prev[productId]
    }));
  };

  // Verificar si un producto tiene descuentos
  const productHasDiscounts = (productId: string) => {
    const hasDiscounts = priceData.some(dataPoint => 
      dataPoint[`${productId}_original`] !== null && 
      dataPoint[`${productId}_original`] !== undefined &&
      dataPoint[`${productId}_original`] > 0
    );
    return hasDiscounts;
  };

  // Calcular dominio del eje Y
  const calculateYAxisDomain = () => {
    if (priceData.length === 0) return ['dataMin', 'dataMax'];
    
    let minValue = Infinity;
    let maxValue = -Infinity;
    
    priceData.forEach(dataPoint => {
      Object.keys(dataPoint).forEach(key => {
        if (key !== 'fecha' && key !== 'dateFormatted' && !key.includes('_hasDiscount')) {
          const value = dataPoint[key];
          if (typeof value === 'number' && !isNaN(value) && value > 0) {
            minValue = Math.min(minValue, value);
            maxValue = Math.max(maxValue, value);
          }
        }
      });
    });
    
    if (minValue === Infinity) return ['dataMin', 'dataMax'];
    
    const margin = (maxValue - minValue) * 0.1;
    return [Math.max(0, minValue - margin), maxValue + margin];
  };

  // Componente personalizado para los puntos
  const CustomDot = (props: any) => {
    const { cx, cy, stroke, payload, dataKey, value } = props;
    
    if (!dataKey || !value) return null;
    
    const isOriginalLine = dataKey.includes('_original');
    const baseProductId = isOriginalLine ? dataKey.replace('_original', '') : dataKey;
    const hasDiscount = payload && payload[`${baseProductId}_original`] > 0;
    
    if (hasDiscount && isOriginalLine) {
      // Triángulo hacia abajo para línea original con descuento
      const size = 3;
      const points = `${cx-size},${cy-size} ${cx+size},${cy-size} ${cx},${cy+size}`;
      return <polygon points={points} fill={stroke} stroke={stroke} strokeWidth="1" />;
    } else {
      // Círculo normal
      const radius = isOriginalLine ? 1.5 : 2.5;
      return <circle cx={cx} cy={cy} r={radius} fill={stroke} stroke={stroke} strokeWidth="1" />;
    }
  };

  // Cargar historial de precios
  const loadPriceHistory = async () => {
    if (comparisonProducts.length === 0) {
      setPriceData([]);
      setProducts({});
      return;
    }

    setLocalLoading(true);
    try {
      const allHistories: {[key: string]: PriceHistoryPoint[]} = {};
      const productInfo: {[key: string]: any} = {};

      // Obtener historial para cada producto
      for (const product of comparisonProducts) {
        // IMPORTANTE: Usar solo id_catalogo real, no comparisonId que tiene timestamp
        const rawId = product.id_catalogo || product.ID_CATALOGO || product.id;
        if (!rawId) continue; // Skip si no hay id real
        let productId = String(rawId);
        const originalProductId = productId;
        
        if (productId) {
          try {
            // Si el producto tiene buybox_id, primero obtener la variante ganadora
            // TEMPORALMENTE DESHABILITADO PARA DEBUGGING
            /*
            if (product.buybox_id && product.buybox_id !== null && 
                product.buybox_id !== '(NULL)' && product.buybox_id !== 'NULL' && 
                product.buybox_id !== '0' && product.buybox_id !== 0) {
              
              
              try {
                // Buscar las variantes del buybox
                const variantsResponse = await api.get(`/api/buybox/${product.buybox_id}/variants`);
                const variantsData = variantsResponse.data;
                
                if (variantsData.variants && variantsData.variants.length > 0) {
                  // Encontrar la variante ganadora
                  const winnerVariant = variantsData.variants.find((v: any) => 
                    v.buybox_winner === 1 || v.buybox_winner === '1' || v.buybox_winner === true
                  );
                  
                  if (winnerVariant && winnerVariant.id_catalogo) {
                    // Usar el id_catalogo de la variante ganadora
                    productId = String(winnerVariant.id_catalogo);
                  }
                }
              } catch (variantsError) {
                console.error('Error fetching buybox variants:', variantsError);
                // Continuar con el productId original si falla
              }
            }
            */
            
            const response = await api.get(`/api/product/${encodeURIComponent(productId)}/price-history`);
            const historyData = response.data.history || response.data;
            const history: PriceHistoryPoint[] = Array.isArray(historyData) ? historyData : [];
            
            if (history.length > 0) {
              // Usar el originalProductId como clave para mantener consistencia
              allHistories[originalProductId] = history;
              
              // Guardar info del producto
              const fullTitle = product.titulo || product.title || product.TITULO || 'Producto sin título';
              const marca = product.Marca || product.marca || product.MARCA || 'Sin marca';
              const sitio = product.sitio || product.site || product.SITIO || '';

              productInfo[originalProductId] = {
                title: fullTitle,
                marca: marca,
                sitio: sitio,
                shortTitle: fullTitle.length > 40 ? fullTitle.substring(0, 40) + '...' : fullTitle,
                displayName: `${marca} - ${fullTitle.length > 25 ? fullTitle.substring(0, 25) + '...' : fullTitle}`,
                isBuyboxWinner: product.buybox_winner === 1 || product.buybox_winner === '1' || product.buybox_winner === true
              };
            }
          } catch (error) {
            console.error(`Error loading history for product ${productId}:`, error);
          }
        }
      }

      // Si no hay historiales, salir
      if (Object.keys(allHistories).length === 0) {
        setPriceData([]);
        setProducts({});
        setLocalLoading(false);
        return;
      }

      // Procesar datos por fecha
      const dateMap: {[key: string]: any} = {};
      
      Object.keys(allHistories).forEach(productIdKey => {
        const productId = String(productIdKey);
        const history = allHistories[productId];
        
        history.forEach(point => {
          const dateKey = point.fecha;
          
          if (!dateMap[dateKey]) {
            // Parse date correctly to avoid timezone issues
            let dateObj: Date;
            if (!dateKey.includes('T')) {
              // Use UTC to avoid timezone issues completely
              const parts = dateKey.split('-');
              if (parts.length === 3) {
                const [year, month, day] = parts.map(Number);
                dateObj = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
              } else {
                dateObj = new Date();
              }
            } else {
              dateObj = new Date(dateKey);
            }

            // Format using UTC to be consistent
            const utcDay = String(dateObj.getUTCDate()).padStart(2, '0');
            const utcMonth = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const utcYear = dateObj.getUTCFullYear();

            dateMap[dateKey] = {
              fecha: dateKey,
              dateFormatted: `${utcDay}/${utcMonth}/${utcYear}`
            };
          }
          
          // The API returns the data either directly or nested under 'data'
          const productData = point.data || point;
          
          // Extraer precios - ajustado para coincidir con la estructura del servidor
          const salePriceRaw = productData.precio || productData.sale_price || productData.precio_venta || productData.price;
          const originalPriceRaw = productData.precioOriginal || productData.original_price || productData.precio_original || productData.ORIGINAL_PRICE;
          const discountedPriceRaw = productData.precioDesc || productData.discounted_price || productData.precio_desc || productData.DISCOUNTED_PRICE;
          
          
          // Parsear precios
          // Los campos son DECIMAL(10,2) - los valores ya están correctos
          const parsePrice = (price: any) => {
            if (!price) return null;
            const parsed = typeof price === 'number' ? price : parseFloat(String(price));
            if (isNaN(parsed)) return null;
            
            // Los precios ya vienen correctamente de la BD
            // No hacer ningún ajuste
            return parsed;
          };
          
          const salePrice = parsePrice(salePriceRaw);
          const originalPrice = parsePrice(originalPriceRaw);
          // Solo procesar si hay precio válido
          if (salePrice && salePrice > 0) {
            // Siempre guardar el precio de venta/actual
            dateMap[dateKey][productId] = salePrice;
            
            // Si hay precio original diferente al precio de venta, guardarlo
            if (originalPrice && originalPrice > salePrice) {
              dateMap[dateKey][`${productId}_original`] = originalPrice;
            }
          }
        });
      });

      // Convertir a array y ordenar por fecha
      const chartData = Object.values(dateMap)
        .sort((a: any, b: any) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
        .filter(item => {
          // Filtrar solo fechas con al menos un precio válido
          return Object.keys(item).some(key => 
            key !== 'fecha' && 
            key !== 'dateFormatted' && 
            typeof item[key] === 'number' && 
            item[key] > 0
          );
        });

      setPriceData(chartData);
      setProducts(productInfo);
      
      
      // Inicializar visibilidad
      const initialVisibility: {[key: string]: boolean} = {};
      Object.keys(productInfo).forEach(productId => {
        initialVisibility[productId] = true;
        initialVisibility[`${productId}_original`] = true;
      });
      setVisibleProducts(initialVisibility);

    } catch (error) {
      console.error('Error loading price history:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  // Efecto para cargar datos
  useEffect(() => {
    if (shouldLoad && !priceHistoryData) {
      loadPriceHistory();
    }
  }, [comparisonProducts, shouldLoad]);

  // Efecto para procesar datos precargados
  useEffect(() => {
    if (priceHistoryData && priceHistoryData.productHistories) {
      // Procesar los datos ya cargados por el componente padre
      const allHistories = priceHistoryData.productHistories;
      const productInfo: {[key: string]: any} = {};
      
      // Procesar datos por fecha
      const dateMap: {[key: string]: any} = {};
      
      Object.keys(allHistories).forEach(productIdKey => {
        const productId = String(productIdKey);
        const history = allHistories[productId];
        
        // Encontrar el producto original para obtener su información
        // IMPORTANTE: Usar solo id_catalogo real, no comparisonId que tiene timestamp
        const originalProduct = comparisonProducts.find(p => {
          const rawId = p.id_catalogo || p.ID_CATALOGO || p.id;
          const pId = rawId ? String(rawId) : null;
          return pId && pId === productId;
        });
        
        if (originalProduct) {
          const fullTitle = originalProduct.titulo || originalProduct.title || originalProduct.TITULO || 'Producto sin título';
          const marca = originalProduct.Marca || originalProduct.marca || originalProduct.MARCA || 'Sin marca';
          const sitio = originalProduct.sitio || originalProduct.site || originalProduct.SITIO || '';

          productInfo[productId] = {
            title: fullTitle,
            marca: marca,
            sitio: sitio,
            shortTitle: fullTitle.length > 40 ? fullTitle.substring(0, 40) + '...' : fullTitle,
            displayName: `${marca} - ${fullTitle.length > 25 ? fullTitle.substring(0, 25) + '...' : fullTitle}`
          };
        }
        
        history.forEach((point: any) => {
          const dateKey = point.fecha;
          
          if (!dateMap[dateKey]) {
            // Parse date correctly to avoid timezone issues
            let dateObj: Date;
            if (!dateKey.includes('T')) {
              // Use UTC to avoid timezone issues completely
              const parts = dateKey.split('-');
              if (parts.length === 3) {
                const [year, month, day] = parts.map(Number);
                dateObj = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
              } else {
                dateObj = new Date();
              }
            } else {
              dateObj = new Date(dateKey);
            }

            // Format using UTC to be consistent
            const utcDay = String(dateObj.getUTCDate()).padStart(2, '0');
            const utcMonth = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const utcYear = dateObj.getUTCFullYear();

            dateMap[dateKey] = {
              fecha: dateKey,
              dateFormatted: `${utcDay}/${utcMonth}/${utcYear}`
            };
          }
          
          // The API returns the data either directly or nested under 'data'
          const productData = point.data || point;
          
          // Extraer precios - ajustado para coincidir con la estructura del servidor
          const salePriceRaw = productData.precio || productData.sale_price || productData.precio_venta || productData.price;
          const originalPriceRaw = productData.precioOriginal || productData.original_price || productData.precio_original || productData.ORIGINAL_PRICE;
          
          // Parsear precios
          // Los campos son DECIMAL(10,2) - los valores ya están correctos
          const parsePrice = (price: any) => {
            if (!price) return null;
            const parsed = typeof price === 'number' ? price : parseFloat(String(price));
            if (isNaN(parsed)) return null;
            
            // Los precios ya vienen correctamente de la BD
            // No hacer ningún ajuste
            return parsed;
          };
          
          const salePrice = parsePrice(salePriceRaw);
          const originalPrice = parsePrice(originalPriceRaw);
          
          // Solo procesar si hay precio válido
          if (salePrice && salePrice > 0) {
            // Siempre guardar el precio de venta/actual
            dateMap[dateKey][productId] = salePrice;
            
            // Si hay precio original diferente al precio de venta, guardarlo
            if (originalPrice && originalPrice > salePrice) {
              dateMap[dateKey][`${productId}_original`] = originalPrice;
            }
          }
        });
      });

      // Convertir a array y ordenar por fecha
      const chartData = Object.values(dateMap)
        .sort((a: any, b: any) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
        .filter(item => {
          // Filtrar solo fechas con al menos un precio válido
          return Object.keys(item).some(key => 
            key !== 'fecha' && 
            key !== 'dateFormatted' && 
            typeof item[key] === 'number' && 
            item[key] > 0
          );
        });

      setPriceData(chartData);
      setProducts(productInfo);
      
      // Inicializar visibilidad
      const initialVisibility: {[key: string]: boolean} = {};
      Object.keys(productInfo).forEach(productId => {
        initialVisibility[productId] = true;
        initialVisibility[`${productId}_original`] = true;
      });
      setVisibleProducts(initialVisibility);
    }
  }, [priceHistoryData, comparisonProducts]);

  // Tooltip personalizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const productPayload = payload.filter((entry: any) => 
      entry && entry.dataKey && entry.value !== null && entry.value !== undefined && entry.value > 0
    );

    if (productPayload.length === 0) return null;

    // Agrupar por producto
    const groupedProducts = productPayload.reduce((groups: any, entry: any) => {
      const isOriginalLine = entry.dataKey.includes('_original');
      const baseProductId = isOriginalLine ? entry.dataKey.replace('_original', '') : entry.dataKey;
      
      if (!groups[baseProductId]) {
        groups[baseProductId] = {
          productId: baseProductId,
          color: entry.color,
          currentPrice: null,
          originalPrice: null,
          productInfo: products[baseProductId]
        };
      }
      
      if (isOriginalLine) {
        groups[baseProductId].originalPrice = entry.value;
      } else {
        groups[baseProductId].currentPrice = entry.value;
      }
      
      return groups;
    }, {});

    return (
      <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg" style={{ position: 'relative', zIndex: 9999 }}>
        <p className="font-medium text-gray-800 mb-3 text-center">Fecha: {label}</p>
        <div className="space-y-2">
          {Object.values(groupedProducts).map((group: any) => (
            <div key={group.productId} className="border-l-4 pl-2" style={{ borderLeftColor: group.color }}>
              <p className="text-sm font-medium">
                {group.productInfo?.marca || 'Sin marca'}
                {group.productInfo?.sitio && (
                  <span className="text-gray-500"> - {group.productInfo.sitio}</span>
                )}
              </p>
              <p className="text-xs text-gray-600">{group.productInfo?.shortTitle || group.productId}</p>
              {group.originalPrice && group.currentPrice && group.originalPrice > group.currentPrice ? (
                <div>
                  <span className="text-sm text-gray-500" style={{ textDecoration: 'line-through', textDecorationStyle: 'dotted' }}>
                    ${group.originalPrice.toLocaleString('es-AR')}
                  </span>
                  <span className="mx-1">→</span>
                  <span className="text-sm font-bold text-red-600">
                    ${group.currentPrice.toLocaleString('es-AR')}
                  </span>
                  <span className="text-xs text-green-600 ml-2">
                    (-{Math.round(((group.originalPrice - group.currentPrice) / group.originalPrice) * 100)}%)
                  </span>
                </div>
              ) : (
                <p className="text-sm font-semibold">${group.currentPrice?.toLocaleString('es-AR')}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="h-6 w-6 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-600">{t('compare.loading_price_history')}</span>
        </div>
      </div>
    );
  }

  if (priceData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="text-gray-500">
          <h3 className="text-lg font-medium text-gray-800 mb-2">{t('compare.no_price_history')}</h3>
          <p className="text-gray-600">{t('compare.no_price_history_help')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('compare.price_evolution')}</h3>
        <p className="text-sm text-gray-600">{t('compare.price_timeline_subtitle')}</p>
      </div>

      <div style={{ width: '100%', height: '300px' }}>
        <ResponsiveContainer>
          <LineChart data={priceData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="dateFormatted" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `$${value?.toLocaleString('es-AR')}`}
              domain={calculateYAxisDomain()}
            />
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
            
            {/* Renderizar líneas para cada producto - usar orderedProductIds para consistencia de colores */}
            {orderedProductIds.map((productId, index) => {
              const color = colors[index % colors.length];
              const isVisible = visibleProducts[productId];
              const isOriginalVisible = visibleProducts[`${productId}_original`];
              const hasDiscounts = productHasDiscounts(productId);
              
              // Verificar si el dataKey existe en los datos
              const hasOriginalData = priceData.length > 0 && priceData.some(point => 
                point[`${productId}_original`] !== undefined && 
                point[`${productId}_original`] !== null
              );
              
              
              return (
                <React.Fragment key={productId}>
                  {/* Línea principal (precio actual) */}
                  {isVisible && (
                    <Line
                      type="monotone"
                      dataKey={productId}
                      stroke={color}
                      strokeWidth={2}
                      dot={<CustomDot stroke={color} />}
                      activeDot={{ r: 4 }}
                      connectNulls={false}
                      name={products[productId]?.marca || productId}
                    />
                  )}
                  
                  {/* Línea punteada (precio original) solo si hay descuentos */}
                  {isOriginalVisible && hasDiscounts && hasOriginalData && (
                    <Line
                      type="monotone"
                      dataKey={`${productId}_original`}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="8 4"
                      dot={<CustomDot stroke={color} />}
                      activeDot={{ r: 3 }}
                      connectNulls={false}
                      opacity={0.6}
                      name={`${products[productId]?.marca || ''} (Original)`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Leyenda */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">{t('compare.products_in_comparison')}</h4>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <div className="w-4 h-0.5 bg-gray-400"></div>
              <span>{t('compare.current_price')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-0.5 bg-gray-400 opacity-70" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, gray 2px, gray 4px)' }}></div>
              <span>{t('compare.original_price')}</span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Usar orderedProductIds para que los colores coincidan con el gráfico */}
          {orderedProductIds.map((productId, index) => {
            const productInfo = products[productId];
            if (!productInfo) return null;
            const hasDiscounts = productHasDiscounts(productId);
            // Buscar producto original usando id_catalogo (no comparisonId)
            const originalProduct = comparisonProducts.find(p => {
              const rawId = p.id_catalogo || p.ID_CATALOGO || p.id;
              return rawId && String(rawId) === String(productId);
            });
            const productImage = originalProduct?.imagen || originalProduct?.image || originalProduct?.IMAGEN || '';
            const productSite = originalProduct?.sitio || originalProduct?.site || originalProduct?.SITIO || '';

            return (
              <div
                key={productId}
                className={`relative flex items-center space-x-3 p-3 border border-gray-200 rounded-lg transition-all cursor-pointer ${
                  visibleProducts[productId] ? 'bg-white hover:shadow-sm' : 'bg-gray-50 opacity-60'
                }`}
                onClick={() => toggleProductVisibility(productId)}
              >
                <div className="absolute top-2 right-2">
                  {visibleProducts[productId] ? <Eye className="w-4 h-4 text-purple-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </div>

                <div className="flex flex-col space-y-1">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors[index % colors.length] }}></div>
                  {hasDiscounts && (
                    <div className="w-4 h-0.5 opacity-70" style={{
                      backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 2px, ${colors[index % colors.length]} 2px, ${colors[index % colors.length]} 4px)`
                    }}></div>
                  )}
                </div>

                {productImage && (
                  <img src={productImage} alt={productInfo.title} className="w-12 h-12 object-cover rounded-lg" />
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{productInfo.marca}</p>
                  <p className="text-xs text-gray-600 truncate">{productInfo.shortTitle}</p>
                  {productSite && <p className="text-xs text-blue-600 truncate">{productSite}</p>}
                  {hasDiscounts && <span className="text-xs text-green-600">Con descuento</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PriceTimeline;